// frontend/web-ui/src/components/lists/picker/ReadingListContentPicker.tsx
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "../../icons/SearchIcon";
import { PickerCard } from "./PickerCard";
import { usePickerInfiniteQuery } from "./usePickerInfiniteQuery";
import type { PickerItem, PickerListType } from "./types";

const SEARCH_DEBOUNCE_MS = 300;

interface ReadingListContentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  listType: PickerListType;
  /** Set of entity ids the parent considers "added". Drives card check state. */
  addedIds: Set<number>;
  /** Click handler for a card — parent decides whether to add or remove. */
  onToggle: (item: PickerItem) => void;
  /** Disables the grid while a parent mutation is in flight. */
  isPending?: boolean;
  /** Custom modal title. Defaults to comics/releases per listType. */
  title?: string;
  /**
   * Optional footer slot rendered below the scrollable grid in both the
   * modal and drawer shells. Used by the edit-mode wrapper to host the
   * batched Save/Discard buttons.
   */
  footer?: React.ReactNode;
  /**
   * When true, the grid container does NOT create its own scroll context
   * — content grows naturally and any overflow is handled by the parent
   * scroll container. Used when the picker body is mounted inline (e.g.
   * the wizard step 3) where a nested scroll would conflict with the
   * outer modal's scroll. Default false (the picker owns its scroll, as
   * required by the modal/drawer shells).
   */
  disableInternalScroll?: boolean;
}

export function ReadingListContentPicker(props: ReadingListContentPickerProps) {
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { ssr: false },
  );
  if (!props.isOpen) return null;
  return isMobile ? <PickerDrawer {...props} /> : <PickerModal {...props} />;
}

function PickerModal(props: ReadingListContentPickerProps) {
  const [t] = useTranslation();
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="lg"
      isCentered
      closeOnOverlayClick={!props.isPending}
      closeOnEsc={!props.isPending}
    >
      <ModalOverlay bg="rgba(0,0,0,0.7)" backdropFilter="blur(8px)" />
      <ModalContent
        bg="charcoal.charcoal"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="14px"
        overflow="hidden"
        maxW="720px"
        maxH="80vh"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          fontFamily="Bricolage Grotesque"
          fontWeight={500}
          fontSize="18px"
          color="dune.dune"
          px="22px"
          pt="18px"
          pb="0"
        >
          {props.title ??
            t(
              props.listType === "releases"
                ? "screens.shelf.readingLists.picker.releasesTitle"
                : "screens.shelf.readingLists.picker.comicsTitle",
            )}
        </ModalHeader>
        <ModalCloseButton color="dune.dune" top="14px" right="14px" />
        <ModalBody
          px="22px"
          py="14px"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ReadingListContentPickerBody {...props} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function PickerDrawer(props: ReadingListContentPickerProps) {
  const [t] = useTranslation();
  return (
    <Drawer
      isOpen={props.isOpen}
      onClose={props.onClose}
      placement="bottom"
      size="full"
    >
      <DrawerOverlay bg="rgba(0,0,0,0.7)" />
      <DrawerContent bg="charcoal.charcoal" display="flex" flexDirection="column">
        <DrawerHeader
          fontFamily="Bricolage Grotesque"
          fontSize="18px"
          color="dune.dune"
        >
          {props.title ??
            t(
              props.listType === "releases"
                ? "screens.shelf.readingLists.picker.releasesTitle"
                : "screens.shelf.readingLists.picker.comicsTitle",
            )}
        </DrawerHeader>
        <DrawerCloseButton color="dune.dune" />
        <DrawerBody
          px="16px"
          pb="16px"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ReadingListContentPickerBody {...props} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * The bare body — search bar + grid + sections — without any modal/drawer
 * shell. Exported so the wizard can mount the picker inline in step 3
 * without nesting modals.
 */
export function ReadingListContentPickerBody({
  listType,
  addedIds,
  onToggle,
  isPending,
  isOpen,
  footer,
  disableInternalScroll,
}: ReadingListContentPickerProps) {
  const [t] = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Reset on close so reopen is fresh.
  useEffect(() => {
    if (!isOpen) {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [isOpen]);

  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [searchInput]);

  const isSearching = debouncedQuery.length > 0;

  const {
    items,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = usePickerInfiniteQuery({
    listType,
    query: debouncedQuery,
    enabled: isOpen,
  });

  // IntersectionObserver-driven infinite scroll.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const placeholder =
    listType === "releases"
      ? t("screens.shelf.readingLists.picker.releasesSearchPlaceholder")
      : t("screens.shelf.readingLists.picker.comicsSearchPlaceholder");

  return (
    <>
      <InputGroup mb="14px" flexShrink={0}>
        <InputLeftElement pointerEvents="none" h="full" pl="6px">
          <SearchIcon color="dune.700" w="16px" h="16px" />
        </InputLeftElement>
        <Input
          autoFocus
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={placeholder}
          pl="40px"
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.20"
          borderRadius="10px"
          color="dune.dune"
          fontSize="15px"
          _placeholder={{ color: "dune.800" }}
          _focus={{
            borderColor: "blaze.blaze",
            bg: "transparent.white.10",
            boxShadow: "none",
          }}
        />
      </InputGroup>

      <Box
        flex={disableInternalScroll ? undefined : "1"}
        overflowY={disableInternalScroll ? "visible" : "auto"}
        pr="4px"
      >
        <PickerSection
          heading={t(
            isSearching
              ? "screens.shelf.readingLists.picker.searchResultsHeading"
              : "screens.shelf.readingLists.picker.defaultFeedHeading",
          )}
        >
          {isLoading && items.length === 0 ? (
            <Flex justify="center" py="32px">
              <Spinner color="blaze.blaze" />
            </Flex>
          ) : isError ? (
            <Text color="dune.700" fontSize="13px" py="24px" textAlign="center">
              {t("screens.shelf.readingLists.picker.feedErrorTitle")}
            </Text>
          ) : items.length === 0 && isSearching ? (
            <Text color="dune.700" fontSize="13px" py="24px" textAlign="center">
              {t("screens.shelf.readingLists.picker.noResultsTitle", {
                term: debouncedQuery,
              })}
            </Text>
          ) : (
            <>
              <PickerGrid
                items={items}
                addedIds={addedIds}
                onToggle={onToggle}
                isPending={isPending}
              />
              <Box ref={sentinelRef} h="1px" />
              {isFetchingNextPage && (
                <Flex justify="center" py="16px">
                  <Spinner color="blaze.blaze" size="sm" />
                </Flex>
              )}
            </>
          )}
        </PickerSection>
      </Box>
      {footer && (
        <Box
          flexShrink={0}
          pt="14px"
          mt="14px"
          borderTop="1px solid"
          borderColor="transparent.white.10"
        >
          {footer}
        </Box>
      )}
    </>
  );
}

function PickerSection({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <Box mb="18px">
      <Text
        fontSize="11px"
        letterSpacing="0.5px"
        textTransform="uppercase"
        fontWeight={600}
        color="dune.700"
        mb="10px"
      >
        {heading}
      </Text>
      {children}
    </Box>
  );
}

function PickerGrid({
  items,
  addedIds,
  onToggle,
  isPending,
}: {
  items: PickerItem[];
  addedIds: Set<number>;
  onToggle: (i: PickerItem) => void;
  isPending?: boolean;
}) {
  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing="12px">
      {items.map((item) => (
        <PickerCard
          key={item.id}
          item={item}
          isAdded={addedIds.has(item.id)}
          onToggle={onToggle}
          isPending={isPending}
        />
      ))}
    </SimpleGrid>
  );
}
